import * as React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useSelector } from 'react-redux'
import { createGlobalStyle } from 'styled-components'
// components
import { DrawerModal, LoaderWithOverlay } from 'components/UI'
// graphql
import { RISK_TEMPLATE_QUERY } from 'graphql/risk/queries'
import { INLINE_FUNCTIONS_QUERY } from 'graphql/systemProcesses/queries'
import { SECURITY_PROFILES_QUERY } from 'graphql/securityProfiles/queries'
import { PLAN_TYPES_SELECT_QUERY } from 'graphql/planType/queries'
import {
  SYSTEM_PROCESS_CREATE_MUTATION,
  SYSTEM_PROCESS_DELETE_MUTATION,
  SYSTEM_PROCESS_UPDATE_MUTATION
} from 'graphql/systemProcesses/mutations'
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
// view components
import SystemProcessCreateForm from './Form'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// hooks
import { useApolloClient, useCurrentUser, useInitialValues, useModalData, useShowModal } from 'hooks/index'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
import { SystemProcessesPageContext } from 'containers/SystemProcesses/Table/layers/API'
// constants
import { Modals } from 'constants/modals'
import { PROCESS_TYPE_OPTIONS } from 'constants/systemProcesses'
import { FORM_SELECT_SECURITY_OPTIONS } from 'constants/security'
// themes
import { CloseIcon } from 'components/UI/Icons'
// types
import { IOption } from 'components/FormElements/Select'
import {
  IUser,
  IField,
  FieldType,
  IPosition,
  FieldSource,
  IRiskTemplate,
  IInlineFunction,
  ISecurityProfile,
  IFieldValueMapItem,
  SystemProcessObject,
  SystemProcessTransactionType
} from 'types/graphql'
// views
import { Header } from 'components/Modals/PlanItems/styles'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// utils
import { get, isEmpty } from 'lodash'
import { getFieldsOptions } from 'utils/form'
import { getOptionsBySource } from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Lookup/utils'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import {
  getSystemProcessInput,
  getRTSystemProcessInput,
  collectUserOrPositionIds,
  getSystemProcessInitials,
  getRTSystemProcessInitials
} from 'utils/systemProcess'

const PAPER_PROPS = {
  style: {
    height: '100%',
    overflow: 'hidden',
    top: 0
  }
}

const GlobalStyles = createGlobalStyle`
    .react-select__menu {
        min-width: 0;
    }

    @media (max-width: 400px) {
        .react-select__menu {
            min-width: 0;
        }
    }
`

const SystemProcessDetailsDrawer: React.FC = () => {
  const [systemProcessCreateMutation] = useMutation(SYSTEM_PROCESS_CREATE_MUTATION)
  const [systemProcessUpdateMutation] = useMutation(SYSTEM_PROCESS_UPDATE_MUTATION)
  const [systemProcessDeleteMutation] = useMutation(SYSTEM_PROCESS_DELETE_MUTATION)

  const [isLoading, setIsLoading] = React.useState(false)

  const user = useCurrentUser()

  const { client } = useApolloClient()

  const objectEntities = useSelector(objectEntitiesSelector)

  const {
    hideModal,
    modalData: { isOpen, isEdit, systemProcess }
  } = useModalData({ modalType: Modals.SYSTEM_PROCESS_CREATE_MODAL })

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MODAL })

  const { refetch } = React.useContext(SystemProcessesPageContext)
  const { isRiskTemplate, riskTemplate, basicFields } = React.useContext(RiskTemplateAPIContext)

  // methods
  const handleFormSubmit = async (data) => {
    try {
      const input = isRiskTemplate ? getRTSystemProcessInput(data, riskTemplate) : getSystemProcessInput(data)

      if (isEdit) {
        await systemProcessUpdateMutation({
          variables: {
            id: systemProcess.id,
            input
          }
        })
      } else {
        await systemProcessCreateMutation({
          variables: {
            input
          }
        })
      }

      await refetch()

      hideModal()
    } catch (error) {
      console.log('[SystemProcessDetailsDrawer.handleFormSubmit]: ', parseGraphQLErrorMessages(error))
    }
  }

  const deleteSystemProcess = async () => {
    try {
      await systemProcessDeleteMutation({
        variables: {
          id: systemProcess.id
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      console.log('[SystemProcessDetailsDrawer.deleteSystemProcess]: ', parseGraphQLErrorMessages(error))
    }
  }

  const handleDeleteClick = () => {
    showModal({
      width: 'sm',
      handleConfirm: deleteSystemProcess
    })
  }

  const getRtProcessInitialValues = async (usersAndPositions: (IUser | IPosition)[]) => {
    const companySecurityProfiles: ISecurityProfile[] = get(
      await client.query({
        query: SECURITY_PROFILES_QUERY,
        variables: { filter: { companyId: user.companyId }, pagination: { pageTake: 1000, pageSkip: 0 } }
      }),
      'data.securityProfiles.data'
    )

    const riskTemplatesIds: string[] = get(riskTemplate, 'fields', [])
      .filter(({ entityId }) => entityId !== riskTemplate.id)
      .map(({ entityId }) => entityId)

    const relatedFormsTemplatesIds: string[] = systemProcess.transactions
      .filter(({ transactionType }) => transactionType === SystemProcessTransactionType.ShowRelatedForm)
      .map(({ relatedForms: { riskTemplateId } }) => riskTemplateId)

    const scheduledRisksTemplatesIds: string[] = systemProcess.transactions
      .filter(({ transactionType }) =>
        [
          SystemProcessTransactionType.CreateRiskRecurrence,
          SystemProcessTransactionType.DisableRiskRecurrence,
          SystemProcessTransactionType.EnableRiskRecurrence,
          SystemProcessTransactionType.AccessFromRelatedForm,
          SystemProcessTransactionType.CreateRelatedForm
        ].includes(transactionType)
      )
      .flatMap((item) => {
        if (item.transactionType === SystemProcessTransactionType.CreateRiskRecurrence) {
          return item.riskRecurrence.riskTemplateId
        }

        if (item.transactionType === SystemProcessTransactionType.DisableRiskRecurrence) {
          return item.disableRiskRecurrence.riskTemplateId
        }

        if (item.transactionType === SystemProcessTransactionType.EnableRiskRecurrence) {
          return item.enableRiskRecurrence.riskTemplateId
        }

        if (item.transactionType === SystemProcessTransactionType.CreateRelatedForm) {
          return item.createRelatedForm.riskTemplateId
        }

        return item.relatedFormAccessList.map(({ riskTemplateId }) => riskTemplateId)
      })

    const parentRiskTemplates = riskTemplate?.relatedRiskTemplatesIdsFromSystemProcesses || []

    const riskTemplates: IRiskTemplate[] = get(
      await client.query({
        fetchPolicy: 'network-only',
        query: RISK_TEMPLATE_QUERY,
        variables: {
          filter: {
            ids: [
              ...riskTemplatesIds,
              ...parentRiskTemplates,
              ...relatedFormsTemplatesIds,
              ...scheduledRisksTemplatesIds
            ]
          }
        }
      }),
      'data.riskTemplates.data'
    )

    const fields: IField[] = systemProcess.transactions
      .filter(({ transactionType }) =>
        [SystemProcessTransactionType.CreateRiskRecurrence, SystemProcessTransactionType.CreateRelatedForm].includes(
          transactionType
        )
      )
      .map(
        (item) =>
          riskTemplates.find(
            (rt) => rt.id === (item.riskRecurrence?.riskTemplateId || item.createRelatedForm?.riskTemplateId)
          )?.fields
      )
      .flat()

    const fieldValues: IFieldValueMapItem[] = systemProcess.transactions
      .filter(({ transactionType }) =>
        [SystemProcessTransactionType.CreateRiskRecurrence, SystemProcessTransactionType.CreateRelatedForm].includes(
          transactionType
        )
      )
      .map((item) => [...(item.riskRecurrence?.fieldValues || []), ...(item.createRelatedForm?.fieldValues || [])])
      .flat()

    const fieldsOptions = (await getFieldsOptions({ client, fields, fieldsValues: fieldValues })) ?? []
    const usersOptions =
      getOptionsBySource([...fields, ...basicFields], null, [
        FieldSource.Users,
        FieldSource.Positions,
        FieldSource.BusinessUnits
      ])?.options ?? []

    const lookupOptions: IOption[] = [...fieldsOptions, ...usersOptions, ...FORM_SELECT_SECURITY_OPTIONS]

    const parentRiskTemplatesFields: IField[] = riskTemplates
      .filter((item) => parentRiskTemplates.includes(item.id))
      .map((item) =>
        item.fields.filter(
          ({ fieldType, source, active }) =>
            active &&
            fieldType === FieldType.Lookup &&
            source.some((src: FieldSource) => src === FieldSource.Users || src === FieldSource.Positions)
        )
      )
      .flat()

    const filteredRiskTemplates = riskTemplates.filter((item) => !parentRiskTemplates.includes(item.id))

    const inlineFunctions: IInlineFunction[] = get(
      await client.query({
        query: INLINE_FUNCTIONS_QUERY,
        variables: { entityId: riskTemplate.id }
      }),
      'data.getInlineFunctions',
      []
    )

    return getRTSystemProcessInitials({
      systemProcess,
      riskTemplate,
      usersAndPositions,
      companySecurityProfiles,
      riskTemplates: filteredRiskTemplates,
      lookupOptions,
      parentRiskTemplatesFields,
      inlineFunctions
    })
  }

  const getInitialValues = async () => {
    if (!isOpen) {
      return
    }

    if (!systemProcess) {
      return isRiskTemplate
        ? {
            object: {
              label: riskTemplate.name,
              value: SystemProcessObject.RiskTemplate
            },
            active: true,
            processType: PROCESS_TYPE_OPTIONS[0]
          }
        : {
            active: true,
            processType: PROCESS_TYPE_OPTIONS[0]
          }
    }

    try {
      setIsLoading(true)

      const ids = collectUserOrPositionIds(systemProcess)

      const usersAndPositions: (IPosition | IUser)[] = get(
        await client.query({
          query: USERS_OR_POSITIONS_QUERY,
          variables: { filter: { companyId: user.companyId, ids } }
        }),
        'data.usersOrPositions.data',
        []
      )

      if (isRiskTemplate) {
        return getRtProcessInitialValues(usersAndPositions)
      }

      const planTypes = get(await client.query({ query: PLAN_TYPES_SELECT_QUERY }), 'data.planTypes.data', [])

      return getSystemProcessInitials({
        planTypes,
        systemProcess,
        objectEntities,
        usersAndPositions
      })
    } catch (errors) {
      console.log('[SystemProcessDetailsDrawer.getInitialValues]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const initialValues = useInitialValues(getInitialValues, systemProcess, isOpen)

  React.useEffect(() => {
    if (isEdit && !isEmpty(initialValues)) {
      setIsLoading(false)
    }
  }, [isEdit, initialValues])

  return (
    <DrawerModal
      width='800px'
      isOpen={isOpen}
      hideModal={hideModal}
      PaperProps={PAPER_PROPS}
      data-testid='system-process-form-drawer'>
      <Header>
        <span>{`${isEdit ? 'Edit' : 'Create'} Process`}</span>
        <CloseIcon onClick={hideModal} />
      </Header>

      {isLoading ? (
        <LoaderWithOverlay isLoading={isLoading} />
      ) : (
        <SystemProcessCreateForm
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          deleteSystemProcess={handleDeleteClick}
        />
      )}

      <ConfirmDeleteModal header={'Delete System Process'} modalType={Modals.CONFIRM_DELETE_MODAL}>
        Are you sure you want to delete this system process?
      </ConfirmDeleteModal>

      {isOpen && <GlobalStyles />}
    </DrawerModal>
  )
}

export default SystemProcessDetailsDrawer
